import { addDays, differenceInCalendarDays } from 'date-fns'
import {
  extractDateFromSelector,
  formatDateOnlyISO,
  getDateFromReactDatePicker,
  MONTH_YEAR_FORMAT_OPTION,
  parseNullableISO
} from '~/common/dateUtils'
import asJqueryPlugin from '~/common/jqueryUtils'
import { DateText } from '~/components/DateText'
import { LocaleSelect } from '~/components/LocaleSelect'
import TimezoneSelect from '~/components/TimezoneSelect'
import Address from '~/components/v2/Address/Address'
import { UncontrolledAddressSubForm } from '~/components/v2/Address/AddressSubForm'
import UncontrolledCompanyAddressForm from '~/components/v2/Address/CompanyAddressForm'
import { UncontrolledPhoneNumberField } from '~/components/v2/PhoneNumberField'
import { Skeleton } from '~/design_system'
import '../common/VndlyAlert'
import { extractPendoGuide, storePendoGuide } from '~/third_party/analytics'
import { renderIntoSharedReactRoot } from '~/common/SharedReactRootManager'
import { JqueryMessageShim } from '~/legacy_shims/JqueryMessageShim'
import { Footer } from '~/components/v2/layouts/Footer'

window.addDays = addDays
window.differenceInCalendarDays = differenceInCalendarDays

window.dateUtils = {
  parseNullableISO,
  formatDateOnlyISO,
  getDateFromReactDatePicker,
  extractDateFromSelector,
  MONTH_YEAR_FORMAT_OPTION
}

/**
 * Wait for an element to exist in the DOM and call a function when it does.
 *
 * @param selector {string} A CSS selector to find the element
 * @param onExists {function} A function to call when the element exists
 * @param timeoutOpts {object} An object with the following options:
 * @param timeoutOpts.timeout {number} The number of milliseconds to wait before giving up, default is 5000
 * @param timeoutOpts.onTimeout {function} A function to call when the timeout is reached
 *
 * @example
 * waitForElement('.my-element', function onExists(element) {
 *   console.log('Element exists:', element)
 * })
 */
window.waitForElement = function waitForElement(selector, onExists, timeoutOpts = undefined) {
  function condition() {
    return document.querySelector(selector)
  }
  window.waitUntil(condition, onExists, timeoutOpts)
}

/**
 * Wait for a condition fn to return truthy and call a function when it does.
 *
 * @param condition {() => boolean} A function that returns a boolean, when it returns true, the onSuccess function is called
 * @param onSuccess {function} A function to call when the element exists
 * @param timeoutOpts {object} An object with the following options:
 * @param timeoutOpts.timeout {number} The number of milliseconds to wait before giving up, default is 5000
 * @param timeoutOpts.onTimeout {function} A function to call when the timeout is reached
 *
 * @example
 * waitUntil(() => window.ReactJobForm, function onSuccess() {
 *   console.log('Now safe to access window.ReactJobForm')
 *   window.ReactJobForm.getFormContext().foo
 * })
 */
window.waitUntil = function waitUntil(condition, onSuccess, { timeout = 5000, interval = 25, onTimeout } = {}) {
  domReady(function () {
    const startTime = performance.now()
    const conditionResult = condition()
    if (conditionResult) {
      onSuccess(conditionResult)
      return
    }

    const intervalId = setInterval(() => {
      if (performance.now() - startTime >= timeout) {
        clearInterval(intervalId)
        if (typeof onTimeout === 'function') onTimeout()
      } else if (condition()) {
        clearInterval(intervalId)
        onSuccess(condition())
      }
    }, interval)
  })
}

function domReady(fn) {
  // If we're early to the party
  document.addEventListener('DOMContentLoaded', fn)
  // If late; I mean on time.
  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    fn()
  }
}

// JqueryMessageShim provides "window.msg()" for old crap that needs translation
let shimTarget = document.getElementById('l10n-legacy-msg-shim-target')
if (!shimTarget) {
  shimTarget = document.createElement('div')
  shimTarget.id = 'l10n-legacy-msg-shim-target'
  document.body.appendChild(shimTarget)
}
renderIntoSharedReactRoot(<JqueryMessageShim />, shimTarget)

// Directly call pendo guide localstorage lookup when rendering shim
window.storePendoGuide = storePendoGuide
extractPendoGuide()

asJqueryPlugin('LocaleSelect', LocaleSelect, { isUncontrolled: true })
asJqueryPlugin('TimezoneSelect', TimezoneSelect, { isUncontrolled: true })
asJqueryPlugin('PhoneNumberField', UncontrolledPhoneNumberField)
asJqueryPlugin('AddressSubForm', UncontrolledAddressSubForm)
asJqueryPlugin('AddressDisplay', Address)
asJqueryPlugin('CompanyAddressForm', UncontrolledCompanyAddressForm)
asJqueryPlugin('Skeleton', Skeleton)
asJqueryPlugin('DateText', DateText)

window.waitForElement('[data-react="Footer"]', element => {
  renderIntoSharedReactRoot(<Footer />, element)
})
